import { IconSet } from '@postscript/components';
import { PERMISSIONS } from 'components/account/AccountView/users/constants';
import {
  BCC,
  ENTITLEMENTS_DATA_INSIGHTS,
} from 'components/admin/utils/feature-flags';
import { FEATURE_KEYS } from 'components/billing/common/constants';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useMemo } from 'react';
import { NavMenuItem } from '../NavMenu';

// Preserve the date filters in the URL when navigating between data-insights pages
const getAnalyticsLink = (url: string) =>
  window.location.pathname.includes('/data-insights')
    ? `${url}${window.location.search}`
    : url;

export const useDataInsightsMenuItems = () => {
  const shopperStatusContext = useShopperStatus();
  const { hasLabsFlag } = usePSLabs();
  const hasEntitlementsFlag = hasLabsFlag(ENTITLEMENTS_DATA_INSIGHTS);

  return useMemo(
    (): NavMenuItem[] => [
      {
        id: 'data-insights',
        label: 'Data & Insights',
        icon: IconSet.Leaderboard,
        isLoading: shopperStatusContext.isLoading,
        ariaLabelledBy: 'nav-data-insights-title',
        restrictions: [PERMISSIONS.limit_sms_sales_agent_access],
        subMenu: [
          {
            link: (): string => getAnalyticsLink(`/data-insights/messages`),
            label: 'Messages',
            id: 'data-insights-messages',
            entitlementKey: hasEntitlementsFlag
              ? 'analytics::messages::view'
              : undefined,
          },
          {
            link: (): string => getAnalyticsLink(`/data-insights/subscribers`),
            label: 'Subscribers',
            id: 'data-insights-subscribers',
            entitlementKey: hasEntitlementsFlag
              ? 'analytics::subscribers::view'
              : undefined,
          },
          {
            link: (): string => getAnalyticsLink(`/data-insights/ai`),
            label: 'Infinity Testing',
            id: 'data-insights-ai',
            labsFeatureFlag: BCC,
          },
          // TODO: Make this an entitlement check when we sort out the entitlements/FF issues
          ...(shopperStatusContext.hasShopperFeatureEnabled
            ? [
                {
                  link: (): string =>
                    getAnalyticsLink(`/data-insights/shopper`),
                  label: 'Shopper',
                  id: 'shopper-trends',
                },
              ]
            : []),
          {
            link: (): string => getAnalyticsLink(`/data-insights/popups`),
            label: 'Popups',
            id: 'popups',
            gatedFeatureKey: FEATURE_KEYS.omni_trends,
            entitlementKey: hasEntitlementsFlag
              ? 'analytics::trends::view'
              : undefined,
          },
          {
            link: (): string => getAnalyticsLink(`/data-insights/sltv`),
            label: 'SLTV',
            id: 'sltv',
            gatedFeatureKey: FEATURE_KEYS.omni_trends,
            entitlementKey: hasEntitlementsFlag
              ? 'analytics::trends::view'
              : undefined,
          },
          {
            link: '/account/reports',
            label: 'Reports',
            id: 'data-insights-reports',
            entitlementKey: hasEntitlementsFlag
              ? 'analytics::reports::view'
              : undefined,
          },
        ],
      },
    ],
    [shopperStatusContext, hasEntitlementsFlag],
  );
};
